import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar, Package, Cube, CheckCircle, Printer } from "@phosphor-icons/react";

export default function Reports() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/daily-assembly?date=${d}`);
      setReport(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(date); /* eslint-disable-next-line */ }, []);

  const onDateChange = (v) => { setDate(v); load(v); };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Daily Assembly Report</h1>
          <p className="text-xs text-zinc-600 mt-1">Boxes assembled and parts consumed on the selected date</p>
        </div>
        <div className="flex items-end gap-2 no-print">
          <div>
            <label className="brut-label flex items-center gap-1"><Calendar size={10} weight="bold" /> Date</label>
            <input type="date" value={date} onChange={(e) => onDateChange(e.target.value)} className="brut-input mt-1" data-testid="report-date" />
          </div>
          <button onClick={() => window.print()} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="print-report-btn">
            <Printer size={14} weight="bold" /> Print
          </button>
        </div>
      </div>

      {loading && <div className="font-display uppercase">Loading...</div>}

      {report && (
        <div className="space-y-6 print-section" data-testid="report-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPI label="Total Boxes" value={report.total_boxes} icon={Cube} />
            <KPI label="Boxes Ready" value={report.boxes_ready} icon={CheckCircle} tag="Not yet packed" />
            <KPI label="Boxes Packed" value={report.boxes_packed} icon={Package} tag="In packing lists" />
            <KPI label="Units Assembled" value={report.total_units_assembled} icon={Cube} tag="Fully assembled" />
          </div>

          <div className="brut-card p-6">
            <h2 className="font-display uppercase text-xl tracking-tight mb-3">By Product</h2>
            <table className="brut-table">
              <thead><tr><th>Part #</th><th>Product</th><th>Boxes</th><th>Total Units</th></tr></thead>
              <tbody>
                {report.by_product.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-zinc-500">No assemblies on {report.date}.</td></tr>}
                {report.by_product.map((p, i) => (
                  <tr key={i} data-testid={`by-product-${p.product_part_number}`}>
                    <td className="font-bold">{p.product_part_number}</td>
                    <td>{p.product_name}</td>
                    <td className="font-mono-ui">{p.boxes}</td>
                    <td className="font-mono-ui font-bold">{p.units}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="brut-card p-6">
            <h2 className="font-display uppercase text-xl tracking-tight mb-3">Boxes Assembled on {report.date}</h2>
            <table className="brut-table">
              <thead><tr><th>Box #</th><th>Product</th><th>Units</th><th>Components</th><th>Time</th><th>Operator</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {report.boxes.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-zinc-500">No boxes.</td></tr>}
                {report.boxes.map((b) => (
                  <tr key={b.id} data-testid={`report-box-${b.box_number}`}>
                    <td className="font-bold">{b.box_number}</td>
                    <td>
                      <div className="font-mono-ui text-xs">{b.product_part_number}</div>
                      <div className="text-xs text-zinc-600">{b.product_name}</div>
                    </td>
                    <td className="font-mono-ui">{b.units_count}</td>
                    <td className="font-mono-ui">{b.components?.length || 0}</td>
                    <td className="font-mono-ui text-xs">{new Date(b.created_at).toLocaleTimeString()}</td>
                    <td>{b.created_by_name}</td>
                    <td>
                      {b.packing_list_number
                        ? <span className="brut-tag brut-tag-green">Packed</span>
                        : <span className="brut-tag brut-tag-yellow">Ready</span>}
                    </td>
                    <td>
                      <Link to={`/boxes/${b.id}`} className="brut-tag brut-tag-white no-print">View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, icon: Icon, tag }) {
  return (
    <div className="brut-card p-4" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="brut-label">{label}</div>
        <Icon size={18} weight="bold" />
      </div>
      <div className="font-display text-4xl tracking-tighter mt-1">{value}</div>
      {tag && <div className="brut-tag brut-tag-yellow mt-1 inline-block">{tag}</div>}
    </div>
  );
}
